import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BopsLogo, ClientLogoIscpg, gs1_mexico_logo_black } from '@Assets';
import { FullPageLoading } from '@Components';
import { ClientConfiguration } from '@Models';
import { RootState } from '@Store';
import config from '../../config';

interface LandingProps {
  isAuthFetching: boolean;
  isConfigFetching?: boolean;
  configuration?: ClientConfiguration;
}

const customLogos = {
  iscpg: {
    logo: ClientLogoIscpg,
    height: 100,
    width: 100,
  },
};

/**
 * Wraps the BopsLogo in the same animated halo used by EmptyState's
 * MotifHalo. Three concentric rings with `animate-es-breathe` (and delayed
 * variants) create the "alive but calm" hero treatment.
 */
const BopsLogoWithHalo = () => (
  <div className="relative w-[200px] h-[200px] flex items-center justify-center">
    <div className="absolute inset-[30px] rounded-full border border-brand-500/35 animate-es-breathe opacity-90" />
    <div className="absolute inset-[10px] rounded-full border border-brand-500/25 animate-es-breathe-delayed opacity-55" />
    <div className="absolute -inset-[15px] rounded-full border border-brand-500/[0.18] animate-es-breathe-delayed-2 opacity-35" />
    <div className="relative z-[2] w-[110px] h-[110px] rounded-full bg-white border border-gray-200 shadow-[0_0_28px_rgba(124,108,246,0.45),0_8px_24px_rgba(16,24,40,0.08)] flex items-center justify-center">
      <BopsLogo className="h-14" />
    </div>
  </div>
);

const getLogo = (logo: string) => {
  if (config.isGs1Flavor) return <img src={gs1_mexico_logo_black} alt="gs1mex-logo" className="h-44 w-auto" />;

  if (logo && logo in customLogos) {
    return (
      <div className="p-4 bg-gray-900 rounded-xl">
        <img
          src={customLogos[logo].logo}
          alt="account-logo"
          height={customLogos[logo].height}
          width={customLogos[logo].width}
        />
      </div>
    );
  }

  return <BopsLogoWithHalo />;
};

const Landing = ({ isAuthFetching, isConfigFetching, configuration }: LandingProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dataReady = configuration?.dataReady;
  const dashboardsCount = configuration?.dashboards?.length ?? 0;

  useEffect(() => {
    // Wait until BOTH the token and the client configuration have finished
    // loading — Landing reads from two independent Redux slices and the
    // configuration may arrive after auth on slow connections. Without
    // tracking both, a GS1 provider could remain stuck on this welcome page
    // because the effect's deps didn't change when configuration finally
    // populated.
    if (isAuthFetching || isConfigFetching) return;
    // GS1 SMB providers mid-provisioning land on the dedicated /onboarding
    // route which renders the "data is processing" view. `dataReady` is a
    // GS1-only field; bops and logyca configs do not set it.
    if (config.isGs1Flavor && dataReady === false) {
      navigate('/onboarding');
      return;
    }
    if (dashboardsCount > 0) navigate('/monitor');
  }, [isAuthFetching, isConfigFetching, dataReady, dashboardsCount]);

  if (isAuthFetching || isConfigFetching) return <FullPageLoading />;

  return (
    <article className="flex flex-1 items-center justify-center w-full bg-background animate-es-fade-in min-h-0">
      <section className="max-w-[640px] w-full text-center flex flex-col items-center gap-10 py-16 px-8">
        <figure>{getLogo(configuration?.logo || '')}</figure>
        <h1 className="text-[34px] md:text-[42px] font-bold tracking-tight leading-[1.1]">
          <span className="block text-gray-700">{t('Global.WelcomeMessage')}</span>
          <span className="block bg-[linear-gradient(90deg,#7c6cf6,#c850c0,#4facfe,#7c6cf6)] bg-[length:200%_auto] bg-clip-text text-transparent animate-es-gradient">
            {t('Global.WelcomeMessage2')}
          </span>
        </h1>
      </section>
    </article>
  );
};

const mapStateToProps = (state: RootState) => {
  const { authorization, client } = state;
  return {
    configuration: client?.configuration,
    isAuthFetching: authorization.isFetching,
    isConfigFetching: client?.isFetching,
  };
};

export default connect(mapStateToProps)(Landing);
